import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SourcedLogo, { SourcedEmblem, AccentUnderline } from '../brand/SourcedLogo';
import './Homepage.css';

/* Hand-drawn arrow between the how-it-works steps (like the deck). */
function StepArrow() {
  return (
    <svg className="step-arrow" viewBox="0 0 120 28" aria-hidden="true">
      <path
        d="M3 18 C 30 10, 70 12, 112 13"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M98 4 C 104 8, 108 11, 113 13 C 107 15, 103 18, 99 23"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* Orange scribble under the handwritten note. */
function Scribble({ width = 90 }) {
  return (
    <svg className="scribble" width={width} height="14" viewBox="0 0 90 14" aria-hidden="true">
      <path
        d="M2 8 C 20 3, 40 11, 60 6 S 84 4, 88 7 M14 12 C 34 9, 56 12, 76 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const PhoneIcon = () => (
  <svg viewBox="0 0 24 40" className="step-icon" aria-hidden="true">
    <rect x="2" y="2" width="20" height="36" rx="4" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M9 6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 40 40" className="step-icon" aria-hidden="true">
    <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="2.2" />
    <path d="M25 25l11 11" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
  </svg>
);

const BagIcon = () => (
  <svg viewBox="0 0 34 40" className="step-icon" aria-hidden="true">
    <path d="M4 12h26l-2 25H6z" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
    <path d="M11 12V9a6 6 0 0 1 12 0v3" fill="none" stroke="currentColor" strokeWidth="2.2" />
  </svg>
);

function Homepage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Editorial photos exported from the Canva deck. They live in public/canva/
  // and the layout only makes room for them once they actually load, so the
  // page still works before they are added.
  const [heroPhoto, setHeroPhoto] = useState(false);
  const [rackPhoto, setRackPhoto] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // iOS Safari only autoplays inline video when the `muted` and
    // `playsinline` *attributes* are present. React sets `muted` as a
    // property only, so add the attributes by hand before calling play().
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const setSpeed = () => { video.playbackRate = 2; };
    const tryPlay = () => {
      setSpeed();
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    };
    // Low Power Mode and data-saver block autoplay; start on first touch.
    const onFirstTouch = () => {
      tryPlay();
      window.removeEventListener('touchstart', onFirstTouch);
      window.removeEventListener('click', onFirstTouch);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && video.paused) tryPlay();
    };

    video.addEventListener('loadeddata', tryPlay);
    video.addEventListener('playing', setSpeed);
    window.addEventListener('touchstart', onFirstTouch, { passive: true });
    window.addEventListener('click', onFirstTouch);
    document.addEventListener('visibilitychange', onVisible);
    tryPlay();
    return () => {
      video.removeEventListener('loadeddata', tryPlay);
      video.removeEventListener('playing', setSpeed);
      window.removeEventListener('touchstart', onFirstTouch);
      window.removeEventListener('click', onFirstTouch);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = '#ebe4d7';
    document.body.style.color = '#151515';
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString(),
      });
    } catch (err) {
      // Still show success. Netlify will retry.
    }
    setSubmitted(true);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const jumpTo = (id) => (e) => {
    e.preventDefault();
    scrollToSection(id);
  };

  return (
    <div className="homepage">
      {/* Navigation */}
      <nav className={`nav${scrolled ? ' nav-scrolled' : ''}`}>
        <div className="nav-inner">
          <a href="/" className="nav-logo" aria-label="Sourced home">
            <SourcedLogo height={26} />
          </a>
          <div className="nav-links">
            <a href="#how-it-works" onClick={jumpTo('how-it-works')}>How it works</a>
            <a href="#styles" onClick={jumpTo('styles')}>Styles</a>
            <a href="#about" onClick={jumpTo('about')}>About</a>
          </div>
          <a href="#waitlist" className="nav-cta" onClick={jumpTo('waitlist')}>
            <span className="nav-cta-label">
              <span>Sign up for beta</span>
              <AccentUnderline width={120} />
            </span>
            <span className="nav-cta-arrow" aria-hidden="true">&#x2192;</span>
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="hero-display">You know<br />the look.</span>
              <span className="hero-serif">We find<br />the pieces.</span>
            </h1>
            <p className="hero-sub">
              Thrift designer fashion across
              <br />
              Depop, Grailed, &amp; more.
              <br />
              No endless searching.
              <br />
              <span className="caps">No</span> guesswork.
              <br />
              Just your vibe, delivered.
            </p>
            <div className="hero-buttons">
              <a href="#waitlist" className="btn btn-primary" onClick={jumpTo('waitlist')}>
                Sign up for the beta
              </a>
              <button type="button" className="btn btn-secondary" onClick={() => scrollToSection('how-it-works')}>
                See how it works
              </button>
            </div>
          </div>

          <div className={`hero-visual${heroPhoto ? ' has-photo' : ''}`}>
            <img
              src={`${process.env.PUBLIC_URL}/canva/hero.jpg`}
              alt=""
              className="hero-photo"
              decoding="async"
              onLoad={() => setHeroPhoto(true)}
              onError={() => setHeroPhoto(false)}
            />
            <div className="phone-frame">
              <div className="phone-notch" />
              <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster={`${process.env.PUBLIC_URL}/canva/phone-screen.jpg`}
                width={886}
                height={1920}
                src={`${process.env.PUBLIC_URL}/Recording.mp4`}
                onLoadedMetadata={(e) => { e.target.playbackRate = 2; }}
                onPlay={(e) => { e.target.playbackRate = 2; }}
                className="phone-video"
                aria-label="Recording of the Sourced feed"
              />
            </div>
            <div className="hero-note">
              <span>Curated <br />finds. <br />For your <br />style.</span>
              <Scribble width={96} />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section section-steps">
        <div className="container-wide reveal">
          <div className="steps-label">
            <span>How<br />it<br />works</span>
            <span className="steps-label-rule" />
          </div>
          <ol className="steps">
            <li className="step">
              <div className="step-head">
                <span className="step-num">01.</span>
                <PhoneIcon />
              </div>
              <h3>Upload your vibe</h3>
              <p>Send a pic, moodboard or style inspo. Anything that shows your style.</p>
            </li>
            <li className="step-gap" aria-hidden="true"><StepArrow /></li>
            <li className="step">
              <div className="step-head">
                <span className="step-num">02.</span>
                <SearchIcon />
              </div>
              <h3>We do the sourcing</h3>
              <p>We search thousands of listings across Depop, Grailed, eBay &amp; more.</p>
            </li>
            <li className="step-gap" aria-hidden="true"><StepArrow /></li>
            <li className="step">
              <div className="step-head">
                <span className="step-num">03.</span>
                <BagIcon />
              </div>
              <h3>You get the finds</h3>
              <p>We send you handpicked pieces you'll actually want to wear.</p>
            </li>
          </ol>
        </div>
      </section>

      {/* Statement */}
      <section className="section section-statement">
        <div className={`statement reveal${rackPhoto ? ' has-photo' : ''}`}>
          <span className="statement-word" aria-hidden="true">Sourced</span>
          <img
            src={`${process.env.PUBLIC_URL}/canva/rack.jpg`}
            alt="A rail of secondhand jackets and shirts in a thrift shop"
            className="statement-photo"
            decoding="async"
            onLoad={() => setRackPhoto(true)}
            onError={() => setRackPhoto(false)}
          />
          <div className="statement-copy">
            <h2 className="statement-heading">
              Stop searching.
              <br />
              Start sourcing.
            </h2>
            <AccentUnderline width={150} className="statement-underline" />
            <p>
              Your time is better spent
              <br />
              wearing the fits,
              <br />
              not searching for them.
            </p>
            <SourcedEmblem className="statement-emblem" height={52} />
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section id="how-it-works" className="section section-alt">
        <div className="container reveal">
          <span className="eyebrow"><span className="eyebrow-num">01</span>The problem</span>
          <h2 className="section-heading">
            You've got the inspo.
            <br />
            <em>But the thrift web is a lot to wrangle.</em>
          </h2>
          <div className="section-body">
            <p>
              Maybe you saved the perfect fit on TikTok or Pinterest. Maybe
              you've got a whole folder of looks.
            </p>
            <p>
              But when it comes time to thrift it? You're lost in a sea of tags,
              overpriced resellers, and endless scrolling.
            </p>
            <p className="emphasis">
              The pieces are out there, but good luck finding them!
            </p>
          </div>
          <blockquote className="callout">
            Goodbye searchbar, hello thriftable feed. Think of us as your thrift
            stylist. Only faster. And obsessed with your moodboard.
          </blockquote>
        </div>
      </section>

      {/* Styles */}
      <section id="styles" className="section">
        <div className="container reveal">
          <span className="eyebrow"><span className="eyebrow-num">02</span>Styles we curate</span>
          <h2 className="section-heading">
            Your style. <em>Real pieces. Real sellers.</em>
          </h2>
          <div className="style-cards">
            <div className="style-card">
              <span className="style-index">A</span>
              <h3>Archival Streetwear</h3>
              <p>Raf, Margiela, Number (N)ine. The grails you keep screenshotting.</p>
            </div>
            <div className="style-card">
              <span className="style-index">B</span>
              <h3>Y2K Minimalism</h3>
              <p>Low rises, clean lines, nothing extra.</p>
            </div>
            <div className="style-card">
              <span className="style-index">C</span>
              <h3>Classic Ralph, Dior, Zegna</h3>
              <p>Tailoring and the heritage staples, all secondhand.</p>
            </div>
          </div>
          <p className="section-footer-text">
            Whichever way your style leans, we pull the real thing from real
            sellers. All secondhand, all premium, all sustainable.
          </p>
        </div>
      </section>

      {/* No More */}
      <section className="section section-alt">
        <div className="container reveal">
          <span className="eyebrow"><span className="eyebrow-num">03</span>What you won't deal with</span>
          <h2 className="section-heading">No more of this.</h2>
          <ul className="nomore-list">
            <li className="nomore-item">
              <span className="nomore-x" aria-hidden="true">&#x2715;</span>
              <span>Generic results</span>
            </li>
            <li className="nomore-item">
              <span className="nomore-x" aria-hidden="true">&#x2715;</span>
              <span>Tag spamming</span>
            </li>
            <li className="nomore-item">
              <span className="nomore-x" aria-hidden="true">&#x2715;</span>
              <span>
                Wasting hours scrolling through listings that don't fit your
                style or standards
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* Founders */}
      <section id="about" className="section">
        <div className="container reveal">
          <span className="eyebrow"><span className="eyebrow-num">04</span>About</span>
          <h2 className="section-heading">Meet <em>Cole &amp; Ryan.</em></h2>
          <div className="founders">
            <div className="founder">
              <img
                src={`${process.env.PUBLIC_URL}/cole.jpeg`}
                alt="Cole"
                width={480}
                height={480}
                loading="lazy"
                decoding="async"
                className="founder-img"
              />
              <span className="founder-name">Cole</span>
            </div>
            <div className="founder">
              <img
                src={`${process.env.PUBLIC_URL}/ryan.jpeg`}
                alt="Ryan"
                width={480}
                height={480}
                loading="lazy"
                decoding="async"
                className="founder-img"
              />
              <span className="founder-name">Ryan</span>
            </div>
          </div>
          <p className="founders-bio">
            One part Google media strategist, one part Amazon Prime Video AI
            practitioner. Cole and Ryan founded Sourced to fix the search fatigue
            of online thrifting. Together they've built a platform that makes
            online thrifting easier than ever before.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section id="waitlist" className="section section-cta">
        <div className="container reveal">
          <SourcedEmblem className="cta-emblem" height={64} />
          <h2 className="cta-heading">
            Be the first
            <br />
            <em>to thrift smarter.</em>
          </h2>
          <p className="cta-sub">Sign up for early access to the Sourced beta.</p>
          <p className="cta-body">
            We're letting a small group of testers in before launch.
            Get first dibs on a feed built around your style, not a search bar.
          </p>
          {!submitted ? (
            <form className="waitlist-form" name="beta-signup" data-netlify="true" onSubmit={handleWaitlist}>
              <input type="hidden" name="form-name" value="beta-signup" />
              <label className="sr-only" htmlFor="waitlist-email">Email address</label>
              <input
                id="waitlist-email"
                type="email"
                name="email"
                className="waitlist-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary btn-lg">
                Sign up for the beta
              </button>
            </form>
          ) : (
            <div className="waitlist-success" role="status">
              <span className="success-check" aria-hidden="true">&#x2713;</span>
              <p>You're in. We'll reach out when the beta is ready.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <span className="footer-brand">
            <SourcedLogo height={16} />
          </span>
          <span className="footer-copy">
            &copy; {new Date().getFullYear()} Sourced Finds LLC. New York, New York
          </span>
          <Link to="/privacy" className="footer-link">
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  );
}

export default Homepage;
